import { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { AutoAwesome } from '@mui/icons-material';

const StyledCard = styled(Card)(({ theme }) => ({
  border: '1px solid #EAECEF',
  borderRadius: theme.spacing(2),
  boxShadow: 'none',
  marginBottom: theme.spacing(3)
}));

const IconContainer = styled(Box)({
  width: 40,
  height: 40,
  borderRadius: 12,
  backgroundColor: 'rgba(79, 70, 229, 0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});

interface AIQuestionGeneratorProps {
  onGenerateQuestions: (url: string) => void;
  isGenerating?: boolean;
}

const AIQuestionGenerator = ({ onGenerateQuestions, isGenerating = false }: AIQuestionGeneratorProps) => {
  const [productUrl, setProductUrl] = useState('');

  const handleGenerate = () => {
    if (productUrl.trim()) {
      onGenerateQuestions(productUrl.trim());
    }
  };

  return (
    <StyledCard>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconContainer>
            <AutoAwesome sx={{ fontSize: 24, color: '#4F46E5' }} />
          </IconContainer>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              fontSize: '1.25rem',
              color: 'text.primary'
            }}
          >
            Generate with AI
          </Typography>
        </Box>

        <Box>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              fontSize: '0.875rem',
              color: 'text.primary',
              mb: 1
            }}
          >
            Product URL
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              fullWidth
              placeholder="https://example.com/product/your-product-name"
              value={productUrl}
              onChange={(e) => setProductUrl(e.target.value)}
              disabled={isGenerating}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  fontSize: '0.875rem',
                  height: '40px'
                }
              }}
            />
            
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={!productUrl.trim() || isGenerating}
              sx={{
                minWidth: 84,
                height: 40,
                backgroundColor: '#4F46E5',
                fontWeight: 600,
                fontSize: '0.875rem',
                textTransform: 'none',
                whiteSpace: 'nowrap',
                borderRadius: '8px',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#4338CA',
                  boxShadow: 'none'
                },
                '&.Mui-disabled': {
                  backgroundColor: '#E5E7EB',
                  color: '#9CA3AF'
                }
              }}
            >
              {isGenerating ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: 'white' }} />
                  <span>Generating...</span>
                </Box>
              ) : (
                'Create Survey Questions'
              )}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </StyledCard>
  );
};

export default AIQuestionGenerator;

